import posthog from 'posthog-js';
import { isDesktop } from '@/lib/platform';
import type { OnboardingStep, UseCase } from '../types/onboarding';
import type { PermissionKind } from '../components/steps/permissions-step';

function platform(): string {
  return isDesktop ? 'desktop' : 'web';
}

export function useOnboardingAnalytics() {
  return {
    started: () =>
      posthog.capture('onboarding_started', { platform: platform() }),

    // stepNumber is the true 1-based ordinal in the current platform's step
    // array (desktop: name=1,calendar=2,permissions=3,about=4;
    //  web: name=1,calendar=2,about=3). The shell supplies index+1 so the
    // value is always correct regardless of platform.
    stepViewed: (s: OnboardingStep, stepNumber: number) =>
      posthog.capture('onboarding_step_viewed', { step: stepNumber, step_name: s }),

    stepCompleted: (s: OnboardingStep, stepNumber: number) =>
      posthog.capture('onboarding_step_completed', { step: stepNumber, step_name: s }),

    stepSkipped: (s: OnboardingStep, stepNumber: number) =>
      posthog.capture('onboarding_step_skipped', { step: stepNumber, step_name: s }),

    useCaseSelected: (useCase: UseCase) =>
      posthog.capture('onboarding_use_case_selected', { use_case: useCase }),

    referralSelected: (referralSource: string) =>
      posthog.capture('onboarding_referral_selected', { referral_source: referralSource }),

    permissionPrompted: (permission: PermissionKind) =>
      posthog.capture('onboarding_permission_prompted', { permission }),

    permissionResult: (permission: PermissionKind, granted: boolean) =>
      posthog.capture('onboarding_permission_result', { permission, granted }),

    // step_name intentionally unused here — this event's taxonomy is { step }
    // only; the name param is kept for call-site symmetry with the other step methods.
    abandonedLoginSwitch: (_stepName: OnboardingStep, stepNumber: number) =>
      posthog.capture('onboarding_abandoned_login_switch', { step: stepNumber }),

    completed: (p: {
      useCase: UseCase | null;
      referralSource: string | null;
      permissionsGranted: number;
    }) => {
      posthog.capture('onboarding_completed', {
        use_case: p.useCase,
        referral_source: p.referralSource,
        permissions_granted: p.permissionsGranted,
        platform: platform(),
      });
      // Set person properties for cohort analysis without touching distinct id.
      // Guard with optional chaining: setPersonProperties is absent in local dev
      // when the PostHog key is unset (posthog-js no-ops gracefully, but the
      // method may not exist on the stub object in tests that don't mock it).
      posthog.setPersonProperties?.({ use_case: p.useCase, referral_source: p.referralSource });
    },
  };
}
